'use client'

import { getApps, initializeApp } from 'firebase/app'
import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  MessagePayload,
  Messaging,
  onMessage,
} from 'firebase/messaging'

import { enableFcm } from '@/lib/configs'
import { getFirebasePublicConfig } from '@/lib/firebase-config'
import { deleteFCMToken, registerFCMToken } from '@/services/fcm'

const VAPID_KEY =
  'BCfuCwHPDLmcwBEkLfP45MH5JVrfjwKPSaUG8Xay5q7zXTlxmO-scnym6ueh0LB6huu9IE8b6HU4v81Q5fFQr0o'

const DEVICE_ID_KEY = 'fcm_device_id'

const LOCALE_TO_FCM_LANG: Record<string, string> = {
  'en-US': 'en',
  'zh-TW': 'zh',
  'ja-JP': 'jp',
}

export type RegisterWebPushNotificationResult = {
  fcmToken: string
  messaging: Messaging
}

const toFCMLang = (locale: string) => {
  return LOCALE_TO_FCM_LANG[locale] ?? 'en'
}

const getFirebaseApp = () => {
  const firebaseConfig = getFirebasePublicConfig()

  if (!firebaseConfig.apiKey || !firebaseConfig.projectId || !firebaseConfig.messagingSenderId) {
    return null
  }

  return getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0]
}

const getOrCreateDeviceId = () => {
  const currentDeviceId = localStorage.getItem(DEVICE_ID_KEY)

  if (currentDeviceId) {
    return currentDeviceId
  }

  const nextDeviceId = `web-${crypto.randomUUID()}`
  localStorage.setItem(DEVICE_ID_KEY, nextDeviceId)
  return nextDeviceId
}

export const hasRegisteredFcmDevice = () => {
  return typeof window !== 'undefined' && localStorage.getItem(DEVICE_ID_KEY) !== null
}

export const getFirebaseMessaging = async () => {
  if (!enableFcm()) {
    return null
  }

  const app = getFirebaseApp()

  if (!app) {
    return null
  }

  const supported = await isSupported()

  if (!supported) {
    return null
  }

  return getMessaging(app)
}

export const registerWebPushNotification = async (
  authToken: string,
  locale: string,
): Promise<RegisterWebPushNotificationResult | null> => {
  if (!enableFcm()) {
    return null
  }

  try {
    const permission = await Notification.requestPermission()

    if (permission !== 'granted') {
      return null
    }

    const messaging = await getFirebaseMessaging()

    if (!messaging) {
      return null
    }

    const fcmToken = await getToken(messaging, { vapidKey: VAPID_KEY })

    if (!fcmToken) {
      return null
    }

    const deviceId = getOrCreateDeviceId()

    await registerFCMToken({
      authToken,
      deviceId,
      deviceToken: fcmToken,
      lang: toFCMLang(locale),
    })

    return { fcmToken, messaging }
  } catch {
    return null
  }
}

export const unregisterWebPushNotification = async (authToken: string): Promise<void> => {
  const deviceId = localStorage.getItem(DEVICE_ID_KEY)

  if (!deviceId) {
    return
  }

  try {
    const messaging = await getFirebaseMessaging()

    if (messaging) {
      await deleteToken(messaging)
    }

    await deleteFCMToken({ authToken, deviceId })
  } catch {
  } finally {
    localStorage.removeItem(DEVICE_ID_KEY)
  }
}

export const unregisterFcmServiceWorker = async (): Promise<void> => {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return
  }

  const registrations = await navigator.serviceWorker.getRegistrations()

  const firebaseMessagingRegistrations = registrations.filter((registration) => {
    const scriptUrl =
      registration.active?.scriptURL ??
      registration.waiting?.scriptURL ??
      registration.installing?.scriptURL ??
      ''

    return scriptUrl.endsWith('/firebase-messaging-sw.js')
  })

  await Promise.all(firebaseMessagingRegistrations.map((registration) => registration.unregister()))
}

export const disableWebPushNotification = async (authToken?: string): Promise<void> => {
  const deviceId = typeof window === 'undefined' ? null : localStorage.getItem(DEVICE_ID_KEY)

  try {
    const app = getFirebaseApp()
    const supported = app ? await isSupported() : false

    if (app && supported) {
      await deleteToken(getMessaging(app))
    }
  } catch {}

  if (authToken && deviceId) {
    try {
      await deleteFCMToken({ authToken, deviceId })
    } catch {}
  }

  if (typeof window !== 'undefined') {
    localStorage.removeItem(DEVICE_ID_KEY)
  }

  await unregisterFcmServiceWorker()
}

export const subscribeToForegroundMessages = (
  messaging: Messaging,
  next: (payload: MessagePayload) => void,
) => {
  return onMessage(messaging, next)
}
